import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';

import type { InkPoint, InkStroke } from '../../domain/types';

/**
 * Schreibfläche für den handschriftlichen Kommentar.
 *
 * Gezeichnet wird mit Pointer Events, weil die dieselbe API für Finger und Apple Pencil
 * bieten und den Stiftdruck mitliefern. PencilKit wäre die native Alternative, ist aus dem
 * WebView aber nicht erreichbar — für einen Kommentar auf einem Prüfungsbogen reicht Canvas.
 *
 * Die Striche werden im Store normiert (0…1) abgelegt, nicht in Pixeln: die Fläche ist Teil
 * eines responsiven Layouts und ändert beim Drehen des Geräts ihre Größe.
 */
export function HandwritingCanvas({
  strokes,
  onStroke,
}: {
  strokes: InkStroke[];
  onStroke: (stroke: InkStroke) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const current = useRef<InkPoint[] | null>(null);

  /** Zeichnet alles neu — bei Größenänderung, Themenwechsel und nach jedem Strich. */
  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(rect.width * dpr)) {
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = getComputedStyle(canvas).color;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const all = current.current ? [...strokes, { points: current.current }] : strokes;

    for (const stroke of all) {
      if (stroke.points.length === 0) continue;
      // Jeder Abschnitt bekommt seine eigene Breite aus dem Druck — eine Linie mit einer
      // einzigen Breite sähe wie ein Filzstift aus, nicht wie Handschrift.
      for (let i = 1; i < stroke.points.length; i += 1) {
        const a = stroke.points[i - 1];
        const b = stroke.points[i];
        ctx.beginPath();
        ctx.lineWidth = 1 + b.p * 2.6;
        ctx.moveTo(a.x * rect.width, a.y * rect.height);
        ctx.lineTo(b.x * rect.width, b.y * rect.height);
        ctx.stroke();
      }
      if (stroke.points.length === 1) {
        const only = stroke.points[0];
        ctx.beginPath();
        ctx.arc(only.x * rect.width, only.y * rect.height, 1 + only.p * 1.3, 0, Math.PI * 2);
        ctx.fillStyle = ctx.strokeStyle;
        ctx.fill();
      }
    }
  };

  useEffect(redraw);

  useEffect(() => {
    const observer = new ResizeObserver(redraw);
    if (canvasRef.current) observer.observe(canvasRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toPoint = (e: ReactPointerEvent<HTMLCanvasElement>): InkPoint => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
      // Finger und Maus melden 0 oder einen Ersatzwert von 0,5 — dann eine feste Breite.
      p: e.pointerType === 'pen' && e.pressure > 0 ? e.pressure : 0.5,
    };
  };

  const down = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    current.current = [toPoint(e)];
    redraw();
  };

  const move = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!current.current) return;
    // Zwischenpunkte holen, sonst wird eine schnelle Bewegung zu einem Polygonzug.
    // Wichtig: das Ergebnis kann leer sein — dann zählt das Ereignis selbst. Ohne diesen
    // Rückfall verschluckt die Fläche ganze Striche.
    const coalesced =
      typeof e.nativeEvent.getCoalescedEvents === 'function'
        ? e.nativeEvent.getCoalescedEvents()
        : [];
    const events = coalesced.length > 0 ? coalesced : [e.nativeEvent];
    const rect = e.currentTarget.getBoundingClientRect();
    for (const raw of events) {
      current.current.push({
        x: (raw.clientX - rect.left) / rect.width,
        y: (raw.clientY - rect.top) / rect.height,
        p: raw.pointerType === 'pen' && raw.pressure > 0 ? raw.pressure : 0.5,
      });
    }
    redraw();
  };

  const up = () => {
    const points = current.current;
    current.current = null;
    if (points && points.length > 0) onStroke({ points });
    redraw();
  };

  return (
    <canvas
      ref={canvasRef}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      // Ohne das scrollt das Blatt unter dem Stift weg, statt zu schreiben.
      style={{ touchAction: 'none', width: '100%', height: '100%', display: 'block' }}
    />
  );
}
